<template>
  <div class="vd-shell">

    <!-- Sidebar -->
    <aside class="vd-sidebar">
      <div class="vd-sidebar-logo">
        <img
          v-if="business && business.logo"
          :src="hostName + business.logo"
          :alt="business.name"
          class="vd-biz-logo"
        />
        <div v-else class="vd-biz-logo-placeholder">
          {{ (business && business.name) ? business.name.charAt(0).toUpperCase() : companyCode.charAt(0) }}
        </div>
        <span class="vd-logo-label">{{ business ? business.name : companyCode }}</span>
        <span class="vd-portal-tag">Vendor Portal</span>
      </div>

      <nav class="vd-nav">
        <a class="vd-nav-link" :class="{ 'vd-nav-link--active': activeTab === 'dashboard' }" @click="activeTab = 'dashboard'">
          <span class="vd-nav-icon">🏠</span> Dashboard
        </a>
        <a class="vd-nav-link" :class="{ 'vd-nav-link--active': activeTab === 'profile' }" @click="activeTab = 'profile'">
          <span class="vd-nav-icon">👤</span> My Profile
        </a>
        <a class="vd-nav-link" :class="{ 'vd-nav-link--active': activeTab === 'events' }" @click="switchToEvents">
          <span class="vd-nav-icon">📦</span> Sourcing Events
        </a>
        <a class="vd-nav-link" :class="{ 'vd-nav-link--active': activeTab === 'orders' }" @click="switchToOrders">
          <span class="vd-nav-icon">🛒</span> Purchase Orders
          <span v-if="orders.length" class="vd-nav-count">{{ orders.length }}</span>
        </a>
        <a class="vd-nav-link" :class="{ 'vd-nav-link--active': activeTab === 'documents' }" @click="activeTab = 'documents'">
          <span class="vd-nav-icon">📁</span> Documents
        </a>
        <a class="vd-nav-link" :class="{ 'vd-nav-link--active': activeTab === 'password' }" @click="activeTab = 'password'">
          <span class="vd-nav-icon">🔒</span> Change Password
        </a>
      </nav>

      <div class="vd-sidebar-footer">
        <button class="vd-logout" @click="logout">Sign Out</button>
      </div>
    </aside>

    <!-- Main -->
    <main class="vd-main">

      <header class="vd-topbar">
        <h1 class="vd-page-title">{{ tabTitle }}</h1>
        <div class="vd-topbar-right">
          <span class="vd-vendor-name">{{ vendor?.name ?? '' }}</span>
        </div>
      </header>

      <div class="vd-content" v-if="!loading && vendor">

        <!-- ── Dashboard tab ── -->
        <template v-if="activeTab === 'dashboard'">
          <div class="vd-welcome">
            <h2>Welcome, {{ vendor.name }}</h2>
            <p>Vendor number: <strong>{{ vendor.vendor_number }}</strong></p>
            <span class="vd-status-badge" :class="`vd-status--${vendor.status}`">
              {{ formatStatus(vendor.status) }}
            </span>
          </div>

          <div class="vd-cards">
            <div class="vd-card">
              <div class="vd-card-label">Vendor Number</div>
              <div class="vd-card-value">{{ vendor.vendor_number }}</div>
            </div>
            <div class="vd-card">
              <div class="vd-card-label">Email</div>
              <div class="vd-card-value">{{ vendor.email }}</div>
            </div>
            <div class="vd-card">
              <div class="vd-card-label">Phone</div>
              <div class="vd-card-value">{{ vendor.phone || '—' }}</div>
            </div>
            <div class="vd-card">
              <div class="vd-card-label">Status</div>
              <div class="vd-card-value">{{ formatStatus(vendor.status) }}</div>
            </div>
          </div>
        </template>

        <!-- ── My Profile tab ── -->
        <template v-if="activeTab === 'profile'">
          <div class="vd-section-card">
            <h3 class="vd-section-head">Contact Information</h3>
            <div class="vd-form-grid">
              <div class="vd-field">
                <label>Vendor Name</label>
                <input :value="vendor.name" disabled />
              </div>
              <div class="vd-field">
                <label>Trading Name</label>
                <input v-model="profileForm.trading_name" placeholder="Trading name" />
              </div>
              <div class="vd-field">
                <label>Email</label>
                <input :value="vendor.email" disabled />
              </div>
              <div class="vd-field">
                <label>Phone</label>
                <input v-model="profileForm.phone" placeholder="Phone number" />
              </div>
              <div class="vd-field">
                <label>Website</label>
                <input v-model="profileForm.website" placeholder="https://..." />
              </div>
            </div>
          </div>

          <div class="vd-section-card">
            <h3 class="vd-section-head">Business Registration</h3>
            <div class="vd-form-grid">
              <div class="vd-field">
                <label>Tax ID / TIN</label>
                <input v-model="profileForm.tax_id" placeholder="Tax ID" />
              </div>
              <div class="vd-field">
                <label>RC Number</label>
                <input v-model="profileForm.rc_number" placeholder="RC Number" />
              </div>
            </div>
          </div>

          <div class="vd-section-card">
            <h3 class="vd-section-head">Location</h3>
            <div class="vd-form-grid">
              <div class="vd-field vd-field--full">
                <label>Address</label>
                <input v-model="profileForm.address" placeholder="Street address" />
              </div>
              <div class="vd-field">
                <label>City</label>
                <input v-model="profileForm.city" placeholder="City" />
              </div>
              <div class="vd-field">
                <label>State</label>
                <input v-model="profileForm.state" placeholder="State" />
              </div>
              <div class="vd-field">
                <label>Country</label>
                <input v-model="profileForm.country" placeholder="Country" />
              </div>
              <div class="vd-field">
                <label>Postal Code</label>
                <input v-model="profileForm.postal_code" placeholder="Postal code" />
              </div>
            </div>
          </div>

          <div class="vd-save-row">
            <button class="vd-btn-save" @click="saveProfile" :disabled="saving">
              {{ saving ? 'Saving...' : 'Save Changes' }}
            </button>
            <span v-if="saveSuccess" class="vd-save-ok">✓ Profile updated</span>
          </div>
        </template>

        <!-- ── Sourcing Events tab ── -->
        <template v-if="activeTab === 'events'">
          <div v-if="eventsLoading" class="vd-loading">Loading sourcing events...</div>

          <div v-else-if="events.length === 0" class="vd-empty">
            <div class="vd-empty-icon">📦</div>
            <p>No sourcing events yet.</p>
            <span>You will be notified when you are invited to a sourcing event.</span>
          </div>

          <div v-else class="vd-ev-list">
            <div v-for="ev in events" :key="ev.id" class="vd-po-card">
              <!-- Card header -->
              <div class="vd-po-card-header" @click="expandedEvent = expandedEvent === ev.id ? null : ev.id">
                <div class="vd-po-card-left">
                  <div class="vd-po-icon-wrap" style="background:#eff6ff; border-color:#bfdbfe;">
                    <span class="vd-po-icon">📦</span>
                  </div>
                  <div>
                    <div class="vd-po-card-title-row">
                      <span class="vd-po-number" style="color:#004AAD;">{{ ev.event_number }}</span>
                      <span class="vd-badge" :class="`vd-badge--${ev.status}`">{{ ev.status }}</span>
                      <span class="vd-badge" :class="`vd-badge--${ev.event_vendors?.[0]?.status}`" v-if="ev.event_vendors?.[0]?.status">
                        My status: {{ ev.event_vendors[0].status }}
                      </span>
                    </div>
                    <div class="vd-po-card-meta">
                      <span>{{ ev.title }}</span>
                      <span class="vd-po-meta-sep">·</span>
                      <span>{{ ev.event_type?.toUpperCase() ?? '—' }}</span>
                      <template v-if="ev.submission_deadline">
                        <span class="vd-po-meta-sep">·</span>
                        <span>Deadline {{ formatDate(ev.submission_deadline) }}</span>
                      </template>
                    </div>
                  </div>
                </div>
                <div class="vd-po-card-right">
                  <span class="vd-po-chevron" :class="{ 'vd-po-chevron--open': expandedEvent === ev.id }">▾</span>
                </div>
              </div>

              <!-- Expanded detail -->
              <div v-if="expandedEvent === ev.id" class="vd-po-detail">
                <!-- Event meta chips -->
                <div class="vd-po-detail-meta">
                  <div v-if="ev.payment_terms" class="vd-po-meta-chip">
                    <span class="vd-po-meta-chip-label">Payment Terms</span>
                    <span class="vd-po-meta-chip-val">{{ ev.payment_terms }}</span>
                  </div>
                  <div v-if="ev.valid_until" class="vd-po-meta-chip">
                    <span class="vd-po-meta-chip-label">Valid Until</span>
                    <span class="vd-po-meta-chip-val">{{ formatDate(ev.valid_until) }}</span>
                  </div>
                  <div v-if="ev.delivery_address" class="vd-po-meta-chip vd-po-meta-chip--full">
                    <span class="vd-po-meta-chip-label">Delivery Address</span>
                    <span class="vd-po-meta-chip-val" style="white-space:pre-line">{{ ev.delivery_address }}</span>
                  </div>
                  <div v-if="ev.instructions" class="vd-po-meta-chip vd-po-meta-chip--full">
                    <span class="vd-po-meta-chip-label">Instructions for Vendors</span>
                    <span class="vd-po-meta-chip-val">{{ ev.instructions }}</span>
                  </div>
                </div>

                <!-- Requested line items (read-only) -->
                <div v-if="ev.lines && ev.lines.length" class="vd-po-lines-wrap">
                  <p class="vd-po-lines-title">
                    Items Requested <span class="vd-po-lines-count">{{ ev.lines.length }}</span>
                  </p>
                  <div class="vd-table-wrap">
                    <table class="vd-table vd-po-lines-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Description</th>
                          <th>Qty</th>
                          <th>UOM</th>
                          <th>Specification</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(line, i) in ev.lines" :key="line.id">
                          <td class="vd-text-muted">{{ i + 1 }}</td>
                          <td>{{ line.description }}</td>
                          <td>{{ line.quantity }}</td>
                          <td class="vd-text-muted">{{ line.uom || '—' }}</td>
                          <td class="vd-text-muted">{{ line.specification || '—' }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                <div v-else class="vd-text-muted" style="font-size:13px; padding:8px 0;">No line items attached to this event.</div>

                <!-- ── Quote / Bid section ── -->
                <div class="vd-quote-section">

                  <!-- Declined state -->
                  <div v-if="ev.event_vendors?.[0]?.status === 'declined'" class="vd-quote-declined">
                    You have declined this invitation.
                  </div>

                  <!-- Event closed / awarded -->
                  <template v-else-if="ev.status !== 'open'">
                    <template v-if="ev.event_vendors?.[0]?.status === 'submitted'">
                      <div class="vd-quote-submitted-banner">
                        ✓ Quote submitted — {{ ev.event_vendors[0].quoted_currency }} {{ fmtNum(ev.event_vendors[0].quoted_total) }}
                        <span class="vd-quote-submitted-date">on {{ formatDate(ev.event_vendors[0].quote_submitted_at) }}</span>
                      </div>
                      <button class="vd-btn-view-bid" @click="toggleBidView(ev)">
                        {{ bidViews[ev.id]?.open ? 'Hide Bid Details' : 'View My Bid' }}
                      </button>
                      <div v-if="bidViews[ev.id]?.loading" class="vd-loading" style="padding:16px;">Loading bid details...</div>
                      <div v-else-if="bidViews[ev.id]?.open">
                        <div class="vd-bid-view">
                          <div class="vd-bid-view-meta">
                            <span class="vd-bid-meta-chip">
                              <span class="vd-bid-meta-label">Currency</span>
                              <span class="vd-bid-meta-val">{{ bidViews[ev.id].currency }}</span>
                            </span>
                            <span class="vd-bid-meta-chip">
                              <span class="vd-bid-meta-label">Total</span>
                              <span class="vd-bid-meta-val vd-bid-total">{{ bidViews[ev.id].currency }} {{ fmtNum(bidViews[ev.id].quoted_total) }}</span>
                            </span>
                          </div>
                          <div class="vd-table-wrap">
                            <table class="vd-table" style="font-size:13px;">
                              <thead>
                                <tr>
                                  <th>#</th>
                                  <th>Description</th>
                                  <th>Qty Requested</th>
                                  <th>Quoted Qty</th>
                                  <th class="vd-ta-r">Unit Price</th>
                                  <th class="vd-ta-r">Line Total</th>
                                  <th>Lead Time</th>
                                  <th>Notes</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr v-for="(line, i) in bidViews[ev.id].lines" :key="line.event_line_id">
                                  <td class="vd-text-muted">{{ i + 1 }}</td>
                                  <td>
                                    {{ line.description }}
                                    <span v-if="line.specification" class="vd-po-line-spec">{{ line.specification }}</span>
                                  </td>
                                  <td class="vd-text-muted">{{ line.qty_requested }} {{ line.uom }}</td>
                                  <td class="vd-fw-bold">{{ fmtNum(line.quantity) }}</td>
                                  <td class="vd-ta-r">{{ fmtNum(line.unit_price) }}</td>
                                  <td class="vd-ta-r vd-fw-bold">{{ fmtNum(line.total) }}</td>
                                  <td class="vd-text-muted">{{ line.lead_time_days ? line.lead_time_days + ' days' : '—' }}</td>
                                  <td class="vd-text-muted" style="max-width:160px;white-space:pre-wrap;">{{ line.notes || '—' }}</td>
                                </tr>
                              </tbody>
                              <tfoot>
                                <tr class="vd-tfoot-total">
                                  <td colspan="5" class="vd-tfoot-label">Grand Total ({{ bidViews[ev.id].currency }})</td>
                                  <td class="vd-tfoot-val vd-tfoot-total-val vd-ta-r">{{ fmtNum(bidViews[ev.id].quoted_total) }}</td>
                                  <td colspan="2"></td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                          <div v-if="bidViews[ev.id].notes" class="vd-bid-cover-note">
                            <span class="vd-bid-meta-label" style="display:block;margin-bottom:4px;">Cover Note</span>
                            <p style="margin:0;font-size:13px;color:#374151;white-space:pre-wrap;">{{ bidViews[ev.id].notes }}</p>
                          </div>
                        </div>
                      </div>
                    </template>
                    <div v-else class="vd-text-muted" style="font-size:13px;">This event is no longer accepting submissions.</div>
                  </template>

                  <!-- Open event — show quote form -->
                  <template v-else>
                    <!-- Submitted: banner + view/update toggle -->
                    <template v-if="ev.event_vendors?.[0]?.status === 'submitted'">
                      <div class="vd-quote-submitted-banner">
                        ✓ Quote submitted — {{ ev.event_vendors[0].quoted_currency }} {{ fmtNum(ev.event_vendors[0].quoted_total) }}
                        <span class="vd-quote-submitted-date">on {{ formatDate(ev.event_vendors[0].quote_submitted_at) }}</span>
                      </div>
                      <div class="vd-bid-action-row">
                        <button class="vd-btn-view-bid" @click="toggleBidView(ev)">
                          {{ bidViews[ev.id]?.open ? 'Hide Bid Details' : 'View My Bid' }}
                        </button>
                        <button v-if="ev.event_vendors?.[0]?.allow_requote" class="vd-btn-open-quote vd-btn-open-quote--sm" @click="initQuoteForm(ev)">
                          Update Quote
                        </button>
                        <span v-else class="vd-bid-locked-note">Bid locked — contact buyer to update</span>
                      </div>
                      <div v-if="bidViews[ev.id]?.loading" class="vd-loading" style="padding:16px;">Loading bid details...</div>
                      <div v-else-if="bidViews[ev.id]?.open">
                        <div class="vd-bid-view">
                          <div class="vd-bid-view-meta">
                            <span class="vd-bid-meta-chip">
                              <span class="vd-bid-meta-label">Currency</span>
                              <span class="vd-bid-meta-val">{{ bidViews[ev.id].currency }}</span>
                            </span>
                            <span class="vd-bid-meta-chip">
                              <span class="vd-bid-meta-label">Total</span>
                              <span class="vd-bid-meta-val vd-bid-total">{{ bidViews[ev.id].currency }} {{ fmtNum(bidViews[ev.id].quoted_total) }}</span>
                            </span>
                          </div>
                          <div class="vd-table-wrap">
                            <table class="vd-table" style="font-size:13px;">
                              <thead>
                                <tr>
                                  <th>#</th>
                                  <th>Description</th>
                                  <th>Qty Requested</th>
                                  <th>Quoted Qty</th>
                                  <th class="vd-ta-r">Unit Price</th>
                                  <th class="vd-ta-r">Line Total</th>
                                  <th>Lead Time</th>
                                  <th>Notes</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr v-for="(line, i) in bidViews[ev.id].lines" :key="line.event_line_id">
                                  <td class="vd-text-muted">{{ i + 1 }}</td>
                                  <td>
                                    {{ line.description }}
                                    <span v-if="line.specification" class="vd-po-line-spec">{{ line.specification }}</span>
                                  </td>
                                  <td class="vd-text-muted">{{ line.qty_requested }} {{ line.uom }}</td>
                                  <td class="vd-fw-bold">{{ fmtNum(line.quantity) }}</td>
                                  <td class="vd-ta-r">{{ fmtNum(line.unit_price) }}</td>
                                  <td class="vd-ta-r vd-fw-bold">{{ fmtNum(line.total) }}</td>
                                  <td class="vd-text-muted">{{ line.lead_time_days ? line.lead_time_days + ' days' : '—' }}</td>
                                  <td class="vd-text-muted" style="max-width:160px;white-space:pre-wrap;">{{ line.notes || '—' }}</td>
                                </tr>
                              </tbody>
                              <tfoot>
                                <tr class="vd-tfoot-total">
                                  <td colspan="5" class="vd-tfoot-label">Grand Total ({{ bidViews[ev.id].currency }})</td>
                                  <td class="vd-tfoot-val vd-tfoot-total-val vd-ta-r">{{ fmtNum(bidViews[ev.id].quoted_total) }}</td>
                                  <td colspan="2"></td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                          <div v-if="bidViews[ev.id].notes" class="vd-bid-cover-note">
                            <span class="vd-bid-meta-label" style="display:block;margin-bottom:4px;">Cover Note</span>
                            <p style="margin:0;font-size:13px;color:#374151;white-space:pre-wrap;">{{ bidViews[ev.id].notes }}</p>
                          </div>
                        </div>
                      </div>
                    </template>

                    <!-- Quote form loading -->
                    <div v-if="quoteForms[ev.id]?.loading" class="vd-loading" style="padding:20px;">Loading quote...</div>

                    <template v-else-if="quoteForms[ev.id]">
                      <div class="vd-quote-form-head">
                        <span>{{ ev.event_vendors?.[0]?.status === 'submitted' ? 'Update Your Quote' : 'Submit Your Quote' }}</span>
                        <div class="vd-quote-currency-wrap">
                          <label>Currency</label>
                          <select v-model="quoteForms[ev.id].currency" class="vd-quote-currency-select">
                            <option value="NGN">NGN</option>
                            <option value="USD">USD</option>
                            <option value="GBP">GBP</option>
                            <option value="EUR">EUR</option>
                          </select>
                        </div>
                      </div>

                      <!-- Per-line pricing -->
                      <div class="vd-table-wrap" style="margin-bottom:16px;">
                        <table class="vd-table vd-quote-table">
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>Description</th>
                              <th>Qty Requested</th>
                              <th>Your Qty</th>
                              <th>Unit Price <span class="vd-quote-req">*</span></th>
                              <th class="vd-ta-r">Line Total</th>
                              <th>Lead Time (days)</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="(qline, qi) in quoteForms[ev.id].lines" :key="qline.event_line_id">
                              <td class="vd-text-muted">{{ qi + 1 }}</td>
                              <td>
                                {{ ev.lines[qi]?.description }}
                                <span v-if="ev.lines[qi]?.specification" class="vd-po-line-spec">{{ ev.lines[qi].specification }}</span>
                              </td>
                              <td class="vd-text-muted">{{ ev.lines[qi]?.quantity }} {{ ev.lines[qi]?.uom }}</td>
                              <td>
                                <input v-model.number="qline.quantity" type="number" min="0.0001" step="any"
                                  class="vd-quote-input vd-quote-input--sm"
                                  :placeholder="ev.lines[qi]?.quantity" />
                              </td>
                              <td>
                                <input v-model.number="qline.unit_price" type="number" min="0" step="any"
                                  class="vd-quote-input" placeholder="0.00" required />
                              </td>
                              <td class="vd-ta-r vd-fw-bold">
                                {{ fmtNum(quoteLineTotal(ev, qi)) }}
                              </td>
                              <td>
                                <input v-model.number="qline.lead_time_days" type="number" min="0" step="1"
                                  class="vd-quote-input vd-quote-input--sm" placeholder="—" />
                              </td>
                            </tr>
                          </tbody>
                          <tfoot>
                            <tr class="vd-tfoot-total">
                              <td colspan="5" class="vd-tfoot-label">Total ({{ quoteForms[ev.id].currency }})</td>
                              <td class="vd-tfoot-val vd-tfoot-total-val vd-ta-r">{{ fmtNum(quoteGrandTotal(ev)) }}</td>
                              <td></td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>

                      <!-- Notes -->
                      <div class="vd-quote-notes-row">
                        <label class="vd-quote-notes-label">Cover Note / Additional Terms</label>
                        <textarea v-model="quoteForms[ev.id].notes" class="vd-quote-notes-input"
                          rows="3" placeholder="Any additional terms, conditions, or notes for the buyer..."></textarea>
                      </div>

                      <!-- Actions -->
                      <div class="vd-quote-actions">
                        <button v-if="ev.event_vendors?.[0]?.status !== 'submitted' && ev.event_vendors?.[0]?.status !== 'declined'"
                          class="vd-btn-decline" :disabled="quoteForms[ev.id].submitting"
                          @click="declineEvent(ev)">
                          Decline Invitation
                        </button>
                        <div style="flex:1"></div>
                        <button class="vd-btn-submit-quote" :disabled="quoteForms[ev.id].submitting"
                          @click="openBidConfirm(ev)">
                          {{ quoteForms[ev.id].submitting ? 'Submitting...' : (ev.event_vendors?.[0]?.status === 'submitted' ? 'Update Quote' : 'Submit Quote') }}
                        </button>
                      </div>

                      <div v-if="quoteForms[ev.id].error" class="vd-pw-error" style="margin-top:10px;">
                        {{ quoteForms[ev.id].error }}
                      </div>
                    </template>

                    <!-- Not yet submitted: show Submit button -->
                    <button v-else-if="ev.event_vendors?.[0]?.status !== 'submitted'" class="vd-btn-open-quote" @click="initQuoteForm(ev)">
                      Submit a Quote
                    </button>
                  </template>
                </div>

                <!-- ── Clarifications ── -->
                <div class="vd-clarif-section">
                  <div class="vd-clarif-header">
                    <span class="vd-clarif-title">Clarifications &amp; Questions</span>
                    <span v-if="clarifications[ev.id]?.items?.length" class="vd-clarif-count">
                      {{ clarifications[ev.id].items.length }}
                    </span>
                  </div>

                  <div v-if="clarifications[ev.id]?.loading" class="vd-loading" style="padding:12px 0; font-size:13px;">
                    Loading clarifications...
                  </div>

                  <template v-else>
                    <div v-if="clarifications[ev.id]?.items?.length" class="vd-clarif-list">
                      <div v-for="c in clarifications[ev.id].items" :key="c.id"
                           class="vd-clarif-item" :class="{ 'vd-clarif-item--mine': c.is_mine }">
                        <div class="vd-clarif-q">
                          <span class="vd-clarif-icon vd-clarif-icon--q">Q</span>
                          <div class="vd-clarif-body">
                            <div class="vd-clarif-text">{{ c.question }}</div>
                            <div class="vd-clarif-meta">
                              <span>{{ c.is_mine ? 'You' : c.asked_by }}</span>
                              <span v-if="c.is_public" class="vd-clarif-badge vd-clarif-badge--public">Public</span>
                              <span class="vd-po-meta-sep">·</span>
                              <span>{{ formatDate(c.created_at) }}</span>
                              <span v-if="!c.answer" class="vd-clarif-badge vd-clarif-badge--pending">Awaiting answer</span>
                            </div>
                          </div>
                        </div>
                        <div v-if="c.answer" class="vd-clarif-a">
                          <span class="vd-clarif-icon vd-clarif-icon--a">A</span>
                          <div class="vd-clarif-body">
                            <div class="vd-clarif-text">{{ c.answer }}</div>
                            <div class="vd-clarif-meta">{{ formatDate(c.answered_at) }}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div v-else class="vd-text-muted" style="font-size:13px; padding:6px 0;">
                      No clarifications yet for this event.
                    </div>

                    <!-- Question submission form — only when event is open -->
                    <div v-if="ev.status === 'open' && clarificationForms[ev.id]" class="vd-clarif-form">
                      <label class="vd-clarif-form-label">Ask a Question</label>
                      <textarea
                        v-model="clarificationForms[ev.id].question"
                        class="vd-quote-notes-input"
                        rows="2"
                        placeholder="Type your question or request for clarification..."
                        :disabled="clarificationForms[ev.id].submitting"
                      ></textarea>
                      <div class="vd-clarif-form-footer">
                        <span v-if="clarificationForms[ev.id].error" class="vd-pw-error" style="margin:0;">
                          {{ clarificationForms[ev.id].error }}
                        </span>
                        <span v-else-if="clarificationForms[ev.id].success" class="vd-clarif-success">
                          Question submitted — the buyer will respond shortly.
                        </span>
                        <span v-else></span>
                        <button
                          class="vd-btn-submit-quote"
                          style="padding:8px 20px; font-size:13px;"
                          :disabled="!clarificationForms[ev.id].question?.trim() || clarificationForms[ev.id].submitting"
                          @click="submitClarificationQuestion(ev)"
                        >
                          {{ clarificationForms[ev.id].submitting ? 'Submitting...' : 'Submit Question' }}
                        </button>
                      </div>
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- ── Purchase Orders tab ── -->
        <template v-if="activeTab === 'orders'">
          <div v-if="ordersLoading" class="vd-loading">Loading purchase orders...</div>

          <div v-else-if="orders.length === 0" class="vd-empty">
            <div class="vd-empty-icon">🛒</div>
            <p>No purchase orders yet.</p>
            <span>Issued purchase orders from {{ business?.name || 'the company' }} will appear here.</span>
          </div>

          <div v-else>
            <!-- Summary cards -->
            <div class="vd-po-summary-row">
              <div class="vd-po-stat">
                <div class="vd-po-stat-val">{{ orders.length }}</div>
                <div class="vd-po-stat-label">Total Orders</div>
              </div>
              <div class="vd-po-stat vd-po-stat--blue">
                <div class="vd-po-stat-val">{{ orders.filter(o => o.status === 'issued').length }}</div>
                <div class="vd-po-stat-label">Issued</div>
              </div>
              <div class="vd-po-stat vd-po-stat--amber">
                <div class="vd-po-stat-val">{{ orders.filter(o => o.status === 'partially_received').length }}</div>
                <div class="vd-po-stat-label">Partial</div>
              </div>
              <div class="vd-po-stat vd-po-stat--green">
                <div class="vd-po-stat-val">{{ orders.filter(o => o.status === 'completed').length }}</div>
                <div class="vd-po-stat-label">Completed</div>
              </div>
            </div>

            <!-- Status filter tabs -->
            <div class="vd-po-tabs">
              <button
                v-for="t in poTabs" :key="t.key"
                class="vd-po-tab" :class="{ 'vd-po-tab--active': poStatusTab === t.key }"
                @click="poStatusTab = t.key"
              >{{ t.label }}</button>
            </div>

            <!-- Orders list -->
            <div class="vd-po-list">
              <div v-if="filteredOrders.length === 0" class="vd-empty" style="margin-top:0">
                <div class="vd-empty-icon">🛒</div>
                <p>No {{ poStatusTab ? poTabs.find(t=>t.key===poStatusTab)?.label.toLowerCase() : '' }} orders.</p>
              </div>
              <div v-for="po in filteredOrders" :key="po.id" class="vd-po-card">
                <!-- Card header -->
                <div class="vd-po-card-header" @click="expandedOrder = expandedOrder === po.id ? null : po.id">
                  <div class="vd-po-card-left">
                    <div class="vd-po-icon-wrap">
                      <span class="vd-po-icon">📋</span>
                    </div>
                    <div>
                      <div class="vd-po-card-title-row">
                        <span class="vd-po-number">{{ po.po_number }}</span>
                        <span class="vd-badge" :class="`vd-badge--${po.status}`">{{ formatPoStatus(po.status) }}</span>
                      </div>
                      <div class="vd-po-card-meta">
                        <span>{{ po.title }}</span>
                        <span class="vd-po-meta-sep">·</span>
                        <span>Issued {{ po.issued_at ? formatDate(po.issued_at) : '—' }}</span>
                        <template v-if="po.expected_delivery_date">
                          <span class="vd-po-meta-sep">·</span>
                          <span>Deliver by {{ formatDate(po.expected_delivery_date) }}</span>
                        </template>
                      </div>
                    </div>
                  </div>
                  <div class="vd-po-card-right">
                    <div class="vd-po-total">{{ po.currency }} {{ fmtNum(po.total) }}</div>
                    <span class="vd-po-chevron" :class="{ 'vd-po-chevron--open': expandedOrder === po.id }">▾</span>
                  </div>
                </div>

                <!-- Expanded detail -->
                <div v-if="expandedOrder === po.id" class="vd-po-detail">
                  <!-- PO meta -->
                  <div class="vd-po-detail-meta">
                    <div v-if="po.payment_terms" class="vd-po-meta-chip">
                      <span class="vd-po-meta-chip-label">Payment Terms</span>
                      <span class="vd-po-meta-chip-val">{{ po.payment_terms }}</span>
                    </div>
                    <div v-if="po.delivery_address" class="vd-po-meta-chip vd-po-meta-chip--full">
                      <span class="vd-po-meta-chip-label">Delivery Address</span>
                      <span class="vd-po-meta-chip-val" style="white-space:pre-line">{{ po.delivery_address }}</span>
                    </div>
                    <div v-if="po.description" class="vd-po-meta-chip vd-po-meta-chip--full">
                      <span class="vd-po-meta-chip-label">Instructions</span>
                      <span class="vd-po-meta-chip-val">{{ po.description }}</span>
                    </div>
                    <div v-if="po.notes" class="vd-po-meta-chip vd-po-meta-chip--full">
                      <span class="vd-po-meta-chip-label">Notes</span>
                      <span class="vd-po-meta-chip-val">{{ po.notes }}</span>
                    </div>
                  </div>

                  <!-- Line items -->
                  <div v-if="po.lines && po.lines.length" class="vd-po-lines-wrap">
                    <p class="vd-po-lines-title">Line Items <span class="vd-po-lines-count">{{ po.lines.length }}</span></p>
                    <div class="vd-table-wrap">
                      <table class="vd-table vd-po-lines-table">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Description</th>
                            <th>Qty</th>
                            <th class="vd-ta-r">Unit Price</th>
                            <th class="vd-ta-r">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(line, i) in po.lines" :key="line.id">
                            <td class="vd-text-muted">{{ i + 1 }}</td>
                            <td>
                              {{ line.description }}
                              <span v-if="line.specification" class="vd-po-line-spec">{{ line.specification }}</span>
                            </td>
                            <td>{{ line.quantity }}<span v-if="line.uom" class="vd-text-muted"> {{ line.uom }}</span></td>
                            <td class="vd-ta-r">{{ fmtNum(line.unit_price) }}</td>
                            <td class="vd-ta-r vd-fw-bold">{{ fmtNum(line.total) }}</td>
                          </tr>
                        </tbody>
                        <tfoot>
                          <tr v-if="po.tax_amount">
                            <td colspan="4" class="vd-tfoot-label">Subtotal</td>
                            <td class="vd-tfoot-val">{{ fmtNum(po.subtotal) }}</td>
                          </tr>
                          <tr v-if="po.tax_amount">
                            <td colspan="4" class="vd-tfoot-label">Tax / VAT</td>
                            <td class="vd-tfoot-val">{{ fmtNum(po.tax_amount) }}</td>
                          </tr>
                          <tr class="vd-tfoot-total">
                            <td colspan="4" class="vd-tfoot-label">Total ({{ po.currency }})</td>
                            <td class="vd-tfoot-val vd-tfoot-total-val">{{ fmtNum(po.total) }}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>

                  <!-- ── Invoice actions ── -->
                  <div class="vd-invoice-row">
                    <template v-if="(invoiceState[po.id]?.invoice ?? po.vendor_invoice)">
                      <span class="vd-invoice-badge">
                        Invoice {{ (invoiceState[po.id]?.invoice ?? po.vendor_invoice).invoice_number }}
                      </span>
                      <button class="vd-btn-invoice vd-btn-invoice--view" @click="openInvoice(po)">
                        View / Share Invoice
                      </button>
                    </template>
                    <button v-else class="vd-btn-invoice" :disabled="invoiceState[po.id]?.generating" @click="generateInvoice(po)">
                      {{ invoiceState[po.id]?.generating ? 'Generating...' : '🧾 Generate Invoice' }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- ── Documents tab ── -->
        <template v-if="activeTab === 'documents'">
          <div v-if="!vendor.documents || vendor.documents.length === 0" class="vd-empty">
            <div class="vd-empty-icon">📁</div>
            <p>No documents uploaded yet.</p>
            <span>Contact the procurement team to upload your compliance documents.</span>
          </div>

          <div v-else class="vd-table-wrap">
            <table class="vd-table">
              <thead>
                <tr>
                  <th>Document Name</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Expiry</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="doc in vendor.documents" :key="doc.id">
                  <td>{{ doc.document_name }}</td>
                  <td>{{ doc.document_type }}</td>
                  <td><span class="vd-badge" :class="`vd-badge--${doc.status}`">{{ doc.status }}</span></td>
                  <td>{{ doc.expiry_date ? formatDate(doc.expiry_date) : '—' }}</td>
                  <td>
                    <a :href="hostName + 'storage/' + doc.file_path" target="_blank" class="vd-doc-link">
                      View
                    </a>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>

        <!-- ── Change Password tab ── -->
        <template v-if="activeTab === 'password'">
          <div class="vd-section-card" style="max-width:480px">
            <h3 class="vd-section-head">Change Password</h3>

            <div v-if="pwError" class="vd-pw-error">{{ pwError }}</div>
            <div v-if="pwSuccess" class="vd-pw-success">✓ Password changed successfully.</div>

            <div class="vd-field" style="margin-bottom:14px">
              <label>Current Password</label>
              <div class="vd-pw-wrap">
                <input :type="showPw.current ? 'text' : 'password'" v-model="pwForm.current_password" placeholder="Enter current password" />
                <button type="button" class="vd-eye" @click="showPw.current = !showPw.current" tabindex="-1">{{ showPw.current ? '🙈' : '👁' }}</button>
              </div>
            </div>

            <div class="vd-field" style="margin-bottom:14px">
              <label>New Password</label>
              <div class="vd-pw-wrap">
                <input :type="showPw.new ? 'text' : 'password'" v-model="pwForm.new_password" placeholder="At least 8 characters" />
                <button type="button" class="vd-eye" @click="showPw.new = !showPw.new" tabindex="-1">{{ showPw.new ? '🙈' : '👁' }}</button>
              </div>
            </div>

            <div class="vd-field" style="margin-bottom:20px">
              <label>Confirm New Password</label>
              <div class="vd-pw-wrap">
                <input :type="showPw.confirm ? 'text' : 'password'" v-model="pwForm.new_password_confirmation" placeholder="Repeat new password" />
                <button type="button" class="vd-eye" @click="showPw.confirm = !showPw.confirm" tabindex="-1">{{ showPw.confirm ? '🙈' : '👁' }}</button>
              </div>
            </div>

            <button
              class="vd-btn-save"
              @click="changePassword"
              :disabled="pwSaving || !pwForm.current_password || !pwForm.new_password || !pwForm.new_password_confirmation"
            >
              {{ pwSaving ? 'Updating...' : 'Update Password' }}
            </button>
          </div>
        </template>

      </div>

      <div class="vd-loading" v-else-if="loading">Loading your dashboard...</div>

      <div class="vd-error" v-else>
        <p>Could not load vendor data. <button @click="fetchMe">Retry</button></p>
      </div>

    </main>

    <!-- Invoice Generation Confirm Modal -->
    <div v-if="invoiceConfirmModal.show" class="vd-modal-overlay" @click.self="invoiceConfirmModal.show = false">
      <div class="vd-modal-box vd-modal-box--sm">
        <div class="vd-modal-header vd-modal-header--invoice">
          <div class="vd-modal-header-icon">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
              <polyline points="10 9 9 9 8 9"></polyline>
            </svg>
          </div>
        </div>
        <div class="vd-modal-body vd-modal-body--center">
          <div class="vd-modal-confirm-title">Generate Invoice?</div>
          <div class="vd-modal-confirm-desc">
            An invoice will be created for this purchase order. You can then view, print, or share it with the buyer.
          </div>
          <div v-if="invoiceConfirmModal.po" class="vd-modal-confirm-meta">
            <div class="vd-modal-confirm-meta-row">
              <span class="vd-modal-confirm-meta-label">PO Number</span>
              <span class="vd-modal-confirm-meta-val">{{ invoiceConfirmModal.po.po_number }}</span>
            </div>
            <div class="vd-modal-confirm-meta-row">
              <span class="vd-modal-confirm-meta-label">Description</span>
              <span class="vd-modal-confirm-meta-val">{{ invoiceConfirmModal.po.title }}</span>
            </div>
            <div class="vd-modal-confirm-meta-row">
              <span class="vd-modal-confirm-meta-label">Amount</span>
              <span class="vd-modal-confirm-meta-val" style="color:#059669;font-size:15px;">
                {{ invoiceConfirmModal.po.currency }} {{ fmtNum(invoiceConfirmModal.po.total) }}
              </span>
            </div>
          </div>
        </div>
        <div class="vd-modal-footer">
          <button class="vd-modal-btn vd-modal-btn--cancel" @click="invoiceConfirmModal.show = false">Cancel</button>
          <button class="vd-modal-btn vd-modal-btn--invoice" @click="confirmGenerateInvoice">
            Generate Invoice
          </button>
        </div>
      </div>
    </div>

    <!-- Bid Submission Confirm Modal -->
    <div v-if="bidConfirmModal.show" class="vd-modal-overlay" @click.self="bidConfirmModal.show = false">
      <div class="vd-modal-box vd-modal-box--sm">
        <div class="vd-modal-header vd-modal-header--primary">
          <div class="vd-modal-header-icon">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </div>
        </div>
        <div class="vd-modal-body vd-modal-body--center">
          <div class="vd-modal-confirm-title">
            {{ bidConfirmModal.isUpdate ? 'Update Your Quote?' : 'Submit Your Quote?' }}
          </div>
          <div class="vd-modal-confirm-desc" v-if="!bidConfirmModal.isUpdate">
            Once submitted, your bid is <strong>final and cannot be edited</strong>. The procurement team will be notified of your submission.
            <br><br>
            If you need to make changes after submission, you must request the buyer to unlock your bid.
          </div>
          <div class="vd-modal-confirm-desc" v-else>
            This will <strong>replace your previous bid</strong> with the updated figures. The procurement team will be notified of the update.
          </div>
          <div v-if="bidConfirmModal.ev" class="vd-modal-confirm-meta">
            <div class="vd-modal-confirm-meta-row">
              <span class="vd-modal-confirm-meta-label">Event</span>
              <span class="vd-modal-confirm-meta-val">{{ bidConfirmModal.ev.event_number }}</span>
            </div>
            <div class="vd-modal-confirm-meta-row">
              <span class="vd-modal-confirm-meta-label">Total</span>
              <span class="vd-modal-confirm-meta-val" style="font-weight:700;color:#004AAD;">
                {{ quoteForms[bidConfirmModal.ev.id]?.currency }}
                {{ fmtNum(quoteGrandTotal(bidConfirmModal.ev)) }}
              </span>
            </div>
          </div>
        </div>
        <div class="vd-modal-footer">
          <button class="vd-modal-btn vd-modal-btn--cancel" @click="bidConfirmModal.show = false">Cancel</button>
          <button class="vd-modal-btn vd-modal-btn--confirm" @click="confirmSubmitQuote">
            {{ bidConfirmModal.isUpdate ? 'Yes, Update Quote' : 'Yes, Submit Quote' }}
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { ref, computed, watch, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import axios from 'axios';
import { useToast } from 'vue-toastification';

export default {
  name: 'VendorDashboard',
  setup() {
    const route       = useRoute();
    const router      = useRouter();
    const toast       = useToast();
    const companyCode = route.params.companyCode?.toUpperCase() ?? '';

    const vendor   = ref(null);
    const business = ref(null);
    const loading  = ref(true);
    const hostName = 'http://127.0.0.1:8000/';

    const activeTab   = ref('dashboard');
    const events        = ref([]);
    const eventsLoading = ref(false);
    const expandedEvent = ref(null);
    const quoteForms    = ref({});
    const bidViews          = ref({});
    const bidConfirmModal        = ref({ show: false, ev: null, isUpdate: false });
    const invoiceConfirmModal    = ref({ show: false, po: null });
    const clarifications      = ref({});
    const clarificationForms  = ref({});
    const invoiceState        = ref({});  // { [po.id]: { generating, invoice } }
    const orders        = ref([]);
    const ordersLoading = ref(false);
    const expandedOrder = ref(null);
    const poStatusTab   = ref('');

    const saving      = ref(false);
    const saveSuccess = ref(false);

    const pwForm = ref({ current_password: '', new_password: '', new_password_confirmation: '' });
    const pwSaving  = ref(false);
    const pwError   = ref('');
    const pwSuccess = ref(false);
    const showPw    = ref({ current: false, new: false, confirm: false });
    const profileForm = ref({
      trading_name: '', phone: '', website: '', tax_id: '',
      rc_number: '', address: '', city: '', state: '', country: '', postal_code: '',
    });

    const tabTitle = computed(() => {
      const map = { dashboard: 'Dashboard', profile: 'My Profile', events: 'Sourcing Events', orders: 'Purchase Orders', documents: 'Documents', password: 'Change Password' };
      return map[activeTab.value] ?? 'Dashboard';
    });

    const filteredOrders = computed(() => {
      if (!poStatusTab.value) return orders.value;
      if (poStatusTab.value === 'paid') return orders.value.filter(o => o.payment_status === 'fully_paid' || o.status === 'closed');
      return orders.value.filter(o => o.status === poStatusTab.value);
    });

    const poTabs = [
      { key: '',                  label: 'All' },
      { key: 'issued',            label: 'Issued' },
      { key: 'partially_received',label: 'Partially Received' },
      { key: 'completed',         label: 'Completed' },
      { key: 'paid',              label: 'Paid' },
    ];

    const authHeaders = () => ({
      Authorization: `Bearer ${localStorage.getItem('vendor_token')}`,
    });

    const fetchMe = async () => {
      loading.value = true;
      const token = localStorage.getItem('vendor_token');
      if (!token) { router.push(`/vendor/${companyCode}/login`); return; }

      try {
        const { data } = await axios.get(`/vendor/${companyCode}/me`, {
          headers: authHeaders(),
        });
        vendor.value   = data.data.vendor;
        business.value = data.data.business;

        profileForm.value = {
          trading_name: vendor.value.trading_name ?? '',
          phone:        vendor.value.phone ?? '',
          website:      vendor.value.website ?? '',
          tax_id:       vendor.value.tax_id ?? '',
          rc_number:    vendor.value.rc_number ?? '',
          address:      vendor.value.address ?? '',
          city:         vendor.value.city ?? '',
          state:        vendor.value.state ?? '',
          country:      vendor.value.country ?? '',
          postal_code:  vendor.value.postal_code ?? '',
        };
      } catch (err) {
        if (err.response?.status === 401) {
          localStorage.removeItem('vendor_token');
          router.push(`/vendor/${companyCode}/login`);
        }
        vendor.value = null;
      } finally {
        loading.value = false;
      }
    };

    const fetchEvents = async () => {
      eventsLoading.value = true;
      try {
        const { data } = await axios.get(`/vendor/${companyCode}/sourcing-events`, {
          headers: authHeaders(),
        });
        events.value = data.data ?? [];
      } catch {
        events.value = [];
      } finally {
        eventsLoading.value = false;
      }
    };

    const switchToEvents = () => {
      activeTab.value = 'events';
      if (events.value.length === 0) fetchEvents();
    };

    const fetchOrders = async () => {
      ordersLoading.value = true;
      try {
        const { data } = await axios.get(`/vendor/${companyCode}/purchase-orders`, {
          headers: authHeaders(),
        });
        orders.value = data.data ?? [];
      } catch {
        orders.value = [];
      } finally {
        ordersLoading.value = false;
      }
    };

    const switchToOrders = () => {
      activeTab.value = 'orders';
      if (orders.value.length === 0) fetchOrders();
    };

    const toggleBidView = async (ev) => {
      const existing = bidViews.value[ev.id];

      // Close if already open
      if (existing?.open) {
        bidViews.value[ev.id] = { ...existing, open: false };
        return;
      }

      // If data already loaded just re-open
      if (existing && !existing.loading && existing.lines) {
        bidViews.value[ev.id] = { ...existing, open: true };
        return;
      }

      bidViews.value[ev.id] = { loading: true, open: true, lines: [], currency: '', quoted_total: 0, notes: '' };

      try {
        const { data } = await axios.get(`/vendor/${companyCode}/sourcing-events/${ev.id}/quote`, {
          headers: authHeaders(),
        });
        const record = data.data;
        const quoteLines = record?.quote_lines ?? [];

        bidViews.value[ev.id] = {
          loading:          false,
          open:             true,
          currency:         record?.quoted_currency || ev.currency || 'NGN',
          quoted_total:     record?.quoted_total    || 0,
          quote_submitted_at: record?.quote_submitted_at,
          notes:            record?.evaluation_notes || '',
          lines: quoteLines.map(ql => {
            const evLine = ev.lines.find(l => l.id === ql.event_line_id) || {};
            return {
              event_line_id: ql.event_line_id,
              description:   evLine.description  || '—',
              specification: evLine.specification || '',
              uom:           evLine.uom           || '',
              qty_requested: evLine.quantity      || 0,
              unit_price:    ql.unit_price,
              quantity:      ql.quantity,
              total:         ql.total,
              lead_time_days:ql.lead_time_days,
              notes:         ql.notes,
            };
          }),
        };
      } catch {
        bidViews.value[ev.id] = { loading: false, open: false, lines: [], currency: '', quoted_total: 0, notes: '' };
        toast.error('Failed to load bid details.');
      }
    };

    const initQuoteForm = async (ev) => {
      const existing = quoteForms.value[ev.id];
      if (existing && !existing.loading) return;

      quoteForms.value[ev.id] = { loading: true, lines: [], currency: ev.currency || 'NGN', notes: '', submitting: false, error: '' };

      try {
        const { data } = await axios.get(`/vendor/${companyCode}/sourcing-events/${ev.id}/quote`, {
          headers: authHeaders(),
        });
        const record = data.data;
        const existingLines = record?.quote_lines ?? [];

        quoteForms.value[ev.id] = {
          loading: false,
          currency: record?.quoted_currency || ev.currency || 'NGN',
          notes: record?.evaluation_notes || '',
          submitting: false,
          error: '',
          lines: ev.lines.map(l => {
            const saved = existingLines.find(ql => ql.event_line_id === l.id);
            return {
              event_line_id:  l.id,
              unit_price:     saved?.unit_price ?? '',
              quantity:       saved?.quantity ?? '',
              lead_time_days: saved?.lead_time_days ?? '',
              notes:          saved?.notes ?? '',
            };
          }),
        };
      } catch {
        quoteForms.value[ev.id] = {
          loading: false,
          currency: ev.currency || 'NGN',
          notes: '',
          submitting: false,
          error: '',
          lines: ev.lines.map(l => ({
            event_line_id: l.id, unit_price: '', quantity: '', lead_time_days: '', notes: '',
          })),
        };
      }
    };

    const quoteLineTotal = (ev, qi) => {
      const form = quoteForms.value[ev.id];
      if (!form) return 0;
      const qline  = form.lines[qi];
      const evline = ev.lines[qi];
      const qty    = qline.quantity > 0 ? qline.quantity : (evline?.quantity ?? 0);
      return qty * (qline.unit_price || 0);
    };

    const quoteGrandTotal = (ev) => {
      const form = quoteForms.value[ev.id];
      if (!form) return 0;
      return form.lines.reduce((sum, _, i) => sum + quoteLineTotal(ev, i), 0);
    };

    const openBidConfirm = (ev) => {
      const form = quoteForms.value[ev.id];
      if (!form) return;
      form.error = '';
      for (const l of form.lines) {
        if (l.unit_price === '' || l.unit_price === null || isNaN(Number(l.unit_price))) {
          form.error = 'Please enter a unit price for every line item.';
          return;
        }
      }
      bidConfirmModal.value = {
        show: true,
        ev,
        isUpdate: ev.event_vendors?.[0]?.status === 'submitted',
      };
    };

    const confirmSubmitQuote = () => {
      const ev = bidConfirmModal.value.ev;
      bidConfirmModal.value.show = false;
      submitQuote(ev);
    };

    const submitQuote = async (ev) => {
      const form = quoteForms.value[ev.id];
      if (!form) return;

      form.error = '';
      form.submitting = true;
      try {
        const { data } = await axios.post(
          `/vendor/${companyCode}/sourcing-events/${ev.id}/quote`,
          {
            currency: form.currency,
            notes:    form.notes,
            lines:    form.lines.map(l => ({
              event_line_id:  l.event_line_id,
              unit_price:     Number(l.unit_price),
              quantity:       l.quantity > 0 ? Number(l.quantity) : undefined,
              lead_time_days: l.lead_time_days > 0 ? Number(l.lead_time_days) : undefined,
              notes:          l.notes || undefined,
            })),
          },
          { headers: authHeaders() }
        );
        // Patch the local event record so the header badge updates immediately
        const idx = events.value.findIndex(e => e.id === ev.id);
        const wasSubmitted = events.value[idx]?.event_vendors?.[0]?.status === 'submitted';
        if (idx !== -1 && events.value[idx].event_vendors?.[0]) {
          events.value[idx].event_vendors[0].status           = 'submitted';
          events.value[idx].event_vendors[0].quoted_total     = data.data.quoted_total;
          events.value[idx].event_vendors[0].quoted_currency  = data.data.quoted_currency;
          events.value[idx].event_vendors[0].quote_submitted_at = data.data.quote_submitted_at;
        }
        toast.success(wasSubmitted ? 'Quote updated successfully' : 'Quote submitted successfully');
        form.submitting = false;
      } catch (err) {
        const msg = err.response?.data?.message || 'Failed to submit quote. Please try again.';
        form.error = msg;
        toast.error(msg);
        form.submitting = false;
      }
    };

    const declineEvent = async (ev) => {
      if (!confirm('Are you sure you want to decline this invitation?')) return;
      try {
        await axios.post(`/vendor/${companyCode}/sourcing-events/${ev.id}/decline`, {}, { headers: authHeaders() });
        const idx = events.value.findIndex(e => e.id === ev.id);
        if (idx !== -1 && events.value[idx].event_vendors?.[0]) {
          events.value[idx].event_vendors[0].status = 'declined';
        }
        delete quoteForms.value[ev.id];
      } catch (err) {
        alert(err.response?.data?.message || 'Failed to decline invitation.');
      }
    };

    const fmtNum = (v) => {
      const n = parseFloat(v);
      const a = isNaN(n) ? 0 : n;
      const [int, dec] = a.toFixed(2).split('.');
      return int.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '.' + dec;
    };

    const saveProfile = async () => {
      saving.value = true;
      saveSuccess.value = false;
      try {
        const { data } = await axios.put(`/vendor/${companyCode}/profile`, profileForm.value, {
          headers: authHeaders(),
        });
        vendor.value = data.data;
        saveSuccess.value = true;
        setTimeout(() => { saveSuccess.value = false; }, 3000);
      } catch {
        // silently fail — user sees no success message
      } finally {
        saving.value = false;
      }
    };

    const changePassword = async () => {
      pwError.value   = '';
      pwSuccess.value = false;
      if (pwForm.value.new_password !== pwForm.value.new_password_confirmation) {
        pwError.value = 'New passwords do not match.'; return;
      }
      pwSaving.value = true;
      try {
        await axios.put(`/vendor/${companyCode}/change-password`, pwForm.value, {
          headers: authHeaders(),
        });
        pwSuccess.value = true;
        pwForm.value = { current_password: '', new_password: '', new_password_confirmation: '' };
        setTimeout(() => { pwSuccess.value = false; }, 4000);
      } catch (err) {
        pwError.value = err.response?.data?.message ?? 'Failed to change password.';
      } finally {
        pwSaving.value = false;
      }
    };

    const logout = () => {
      localStorage.removeItem('vendor_token');
      localStorage.removeItem('vendor_company_code');
      localStorage.removeItem('vendor_data');
      router.push(`/vendor/${companyCode}/login`);
    };

    const formatStatus = (s) => {
      const map = {
        registered: 'Registered', prospect: 'Prospect', due_diligence: 'Due Diligence',
        qualified: 'Qualified', approved: 'Approved', suspended: 'Suspended',
        blocked: 'Blocked', in_workflow: 'In Workflow', rejected: 'Rejected',
      };
      return map[s] ?? s;
    };

    const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

    const formatPoStatus = (s) => {
      const map = { issued: 'Issued', partially_received: 'Partially Received', completed: 'Completed', closed: 'Closed' };
      return map[s] ?? s;
    };

    const generateInvoice = (po) => {
      if (invoiceState.value[po.id]?.generating) return;
      invoiceConfirmModal.value = { show: true, po };
    };

    const confirmGenerateInvoice = async () => {
      const po = invoiceConfirmModal.value.po;
      invoiceConfirmModal.value.show = false;
      if (!po) return;

      invoiceState.value[po.id] = { generating: true, invoice: invoiceState.value[po.id]?.invoice ?? null };
      try {
        const { data } = await axios.post(
          `/vendor/${companyCode}/purchase-orders/${po.id}/invoice`,
          {},
          { headers: authHeaders() }
        );
        invoiceState.value[po.id] = { generating: false, invoice: data.data };
        // Patch the order list so the button state reflects immediately
        const idx = orders.value.findIndex(o => o.id === po.id);
        if (idx !== -1) orders.value[idx].vendor_invoice = data.data;
        toast.success('Invoice generated — ' + data.data.invoice_number);
      } catch (err) {
        invoiceState.value[po.id] = { generating: false, invoice: invoiceState.value[po.id]?.invoice ?? null };
        toast.error(err.response?.data?.message || 'Failed to generate invoice.');
      }
    };

    const openInvoice = (po) => {
      const inv = invoiceState.value[po.id]?.invoice ?? po.vendor_invoice;
      if (!inv) return;
      window.open(`/vendor/${companyCode}/invoice/${inv.id}`, '_blank');
    };

    const loadClarifications = async (ev) => {
      clarifications.value[ev.id] = { loading: true, items: [], error: null };
      try {
        const { data } = await axios.get(`/vendor/${companyCode}/sourcing-events/${ev.id}/clarifications`, {
          headers: authHeaders(),
        });
        clarifications.value[ev.id] = { loading: false, items: data.data ?? [], error: null };
      } catch {
        clarifications.value[ev.id] = { loading: false, items: [], error: 'Failed to load clarifications.' };
      }
    };

    const submitClarificationQuestion = async (ev) => {
      const form = clarificationForms.value[ev.id];
      if (!form || !form.question.trim()) return;

      form.submitting = true;
      form.error = '';
      form.success = false;
      try {
        const { data } = await axios.post(
          `/vendor/${companyCode}/sourcing-events/${ev.id}/clarifications`,
          { question: form.question },
          { headers: authHeaders() }
        );
        if (!clarifications.value[ev.id]) {
          clarifications.value[ev.id] = { loading: false, items: [], error: null };
        }
        clarifications.value[ev.id].items.push(data.data);
        form.question = '';
        form.success = true;
        toast.success('Question submitted — the buyer will respond shortly.');
        setTimeout(() => { form.success = false; }, 4000);
      } catch (err) {
        form.error = err.response?.data?.message || 'Failed to submit question.';
      } finally {
        form.submitting = false;
      }
    };

    watch(expandedEvent, (newId) => {
      if (newId) {
        const ev = events.value.find(e => e.id === newId);
        if (ev) {
          if (!clarifications.value[newId]) {
            loadClarifications(ev);
          }
          if (!clarificationForms.value[newId]) {
            clarificationForms.value[newId] = { question: '', submitting: false, error: '', success: false };
          }
        }
      }
    });

    onMounted(fetchMe);

    return {
      companyCode, vendor, business, loading, hostName,
      activeTab, tabTitle, events, eventsLoading, expandedEvent,
      quoteForms, initQuoteForm, quoteLineTotal, quoteGrandTotal, submitQuote, declineEvent,
      bidViews, toggleBidView,
      bidConfirmModal, openBidConfirm, confirmSubmitQuote,
      invoiceConfirmModal, confirmGenerateInvoice,
      clarifications, clarificationForms, loadClarifications, submitClarificationQuestion,
      invoiceState, generateInvoice, openInvoice,
      orders, ordersLoading, expandedOrder, poStatusTab, filteredOrders, poTabs,
      profileForm, saving, saveSuccess,
      pwForm, pwSaving, pwError, pwSuccess, showPw,
      fetchMe, switchToEvents, switchToOrders, saveProfile, changePassword, logout,
      formatStatus, formatDate, formatPoStatus, fmtNum,
    };
  },
};
</script>

<style scoped>
.vd-shell {
  display: flex;
  min-height: 100vh;
  background: #f3f4f6;
  font-family: system-ui, -apple-system, sans-serif;
}

/* ── Sidebar ── */
.vd-sidebar {
  width: 240px;
  background: #064e3b;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
}
.vd-sidebar-logo {
  padding: 20px 20px 18px;
  border-bottom: 1px solid rgba(255,255,255,.1);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}
.vd-biz-logo {
  width: 52px;
  height: 52px;
  object-fit: contain;
  border-radius: 10px;
  background: #fff;
  padding: 4px;
  border: 1px solid rgba(255,255,255,.15);
}
.vd-biz-logo-placeholder {
  width: 52px;
  height: 52px;
  border-radius: 10px;
  background: rgba(255,255,255,.15);
  border: 1px solid rgba(255,255,255,.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  font-weight: 800;
  color: #fff;
}
.vd-logo-label  { font-size: 14px; font-weight: 700; color: #fff !important; line-height: 1.2; }
.vd-portal-tag  { font-size: 10px; font-weight: 700; letter-spacing: .1em; color: #6ee7b7 !important; text-transform: uppercase; }

.vd-nav { padding: 16px 0; flex: 1; }
.vd-nav-link {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 20px;
  color: rgba(255,255,255,.65) !important;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none !important;
  cursor: pointer;
  transition: background .15s, color .15s;
}
.vd-nav-link:hover       { background: rgba(255,255,255,.07); color: #fff !important; }
.vd-nav-link--active     { background: rgba(255,255,255,.12); color: #fff !important; }
.vd-nav-icon             { font-size: 16px; }

.vd-sidebar-footer { padding: 16px 20px; border-top: 1px solid rgba(255,255,255,.1); }
.vd-logout {
  width: 100%;
  padding: 9px;
  background: rgba(255,255,255,.08);
  color: rgba(255,255,255,.75);
  border: 1px solid rgba(255,255,255,.15);
  border-radius: 7px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background .15s;
  font-family: inherit;
}
.vd-logout:hover { background: rgba(255,255,255,.15); color: #fff; }

/* ── Main ── */
.vd-main { flex: 1; display: flex; flex-direction: column; overflow: hidden; }
.vd-topbar {
  background: #fff;
  padding: 16px 28px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #e5e7eb;
}
.vd-page-title  { font-size: 18px; font-weight: 700; color: #1a2b45; margin: 0; }
.vd-vendor-name { font-size: 13.5px; font-weight: 600; color: #374151; }

.vd-content { padding: 28px; overflow-y: auto; }

/* ── Welcome ── */
.vd-welcome {
  background: #fff;
  border-radius: 12px;
  padding: 24px 28px;
  margin-bottom: 24px;
  border-left: 4px solid #059669;
}
.vd-welcome h2 { font-size: 20px; font-weight: 700; color: #1a2b45; margin: 0 0 6px; }
.vd-welcome p  { font-size: 14px; color: #6b7280; margin: 0 0 12px; }

.vd-status-badge { display: inline-block; font-size: 12px; font-weight: 700; padding: 3px 10px; border-radius: 12px; }
.vd-status--approved    { background: #d1fae5; color: #064e3b; }
.vd-status--in_workflow { background: #fef3c7; color: #78350f; }
.vd-status--rejected    { background: #fee2e2; color: #991b1b; }
.vd-status--suspended   { background: #fef3c7; color: #78350f; }
.vd-status--blocked     { background: #fee2e2; color: #991b1b; }

/* ── Stat cards ── */
.vd-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}
.vd-card { background: #fff; border-radius: 10px; padding: 18px 20px; border: 1px solid #e5e7eb; }
.vd-card-label { font-size: 12px; font-weight: 600; color: #9ca3af; text-transform: uppercase; letter-spacing: .05em; margin-bottom: 6px; }
.vd-card-value { font-size: 16px; font-weight: 700; color: #1a2b45; word-break: break-all; }

/* ── Profile form ── */
.vd-section-card {
  background: #fff;
  border-radius: 12px;
  padding: 24px 28px;
  margin-bottom: 20px;
  border: 1px solid #e5e7eb;
}
.vd-section-head {
  font-size: 14px;
  font-weight: 700;
  color: #1a2b45;
  margin: 0 0 18px;
  padding-bottom: 10px;
  border-bottom: 1px solid #f3f4f6;
}
.vd-form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}
.vd-field { display: flex; flex-direction: column; gap: 5px; }
.vd-field--full { grid-column: 1 / -1; }
.vd-field label { font-size: 12.5px; font-weight: 600; color: #374151; }
.vd-field input {
  padding: 9px 12px;
  border: 1px solid #dde5f0;
  border-radius: 7px;
  font-size: 13.5px;
  color: #1a2b45;
  outline: none;
  font-family: inherit;
  transition: border-color .15s;
}
.vd-field input:focus   { border-color: #059669; box-shadow: 0 0 0 3px rgba(5,150,105,.08); }
.vd-field input:disabled { background: #f9fafb; color: #9ca3af; cursor: not-allowed; }

.vd-save-row { display: flex; align-items: center; gap: 14px; margin-top: 4px; }
.vd-btn-save {
  padding: 10px 28px;
  background: #059669;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
  transition: background .15s;
}
.vd-btn-save:hover:not(:disabled) { background: #047857; }
.vd-btn-save:disabled { opacity: .5; cursor: not-allowed; }
.vd-save-ok { font-size: 13px; font-weight: 600; color: #059669; }

/* ── Table ── */
.vd-table-wrap { background: #fff; border-radius: 12px; overflow: hidden; border: 1px solid #e5e7eb; }
.vd-table { width: 100%; border-collapse: collapse; font-size: 13.5px; }
.vd-table th {
  text-align: left;
  padding: 12px 16px;
  font-size: 12px;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: .04em;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}
.vd-table td { padding: 12px 16px; color: #374151; border-bottom: 1px solid #f3f4f6; }
.vd-table tbody tr:last-child td { border-bottom: none; }
.vd-table tbody tr:hover { background: #fafafa; }
.vd-mono { font-family: monospace; font-size: 12.5px; }

.vd-badge { display: inline-block; font-size: 11.5px; font-weight: 700; padding: 2px 9px; border-radius: 10px; background: #f3f4f6; color: #374151; }
.vd-badge--open, .vd-badge--issued   { background: #dbeafe; color: #1e40af; }
.vd-badge--closed, .vd-badge--awarded { background: #d1fae5; color: #064e3b; }
.vd-badge--draft                      { background: #f3f4f6; color: #6b7280; }
.vd-badge--invited                    { background: #ede9fe; color: #5b21b6; }
.vd-badge--submitted                  { background: #fef3c7; color: #92400e; }
.vd-badge--valid                      { background: #d1fae5; color: #064e3b; }
.vd-badge--expired                    { background: #fee2e2; color: #991b1b; }
.vd-badge--expiring_soon              { background: #fef3c7; color: #92400e; }

.vd-doc-link { color: #059669; font-weight: 600; font-size: 13px; text-decoration: none; }
.vd-doc-link:hover { text-decoration: underline; }

/* ── Empty state ── */
.vd-empty {
  text-align: center;
  padding: 60px 24px;
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
}
.vd-empty-icon { font-size: 40px; margin-bottom: 14px; }
.vd-empty p    { font-size: 16px; font-weight: 600; color: #1a2b45; margin: 0 0 6px; }
.vd-empty span { font-size: 13.5px; color: #9ca3af; }

/* ── Password tab ── */
.vd-pw-wrap { position: relative; display: flex; align-items: center; }
.vd-pw-wrap input { width: 100%; padding-right: 40px; }
.vd-eye { position: absolute; right: 10px; border: none; background: transparent; cursor: pointer; font-size: 14px; padding: 0; line-height: 1; }
.vd-pw-error   { background: #fff5f5; border-left: 3px solid #e03535; border-radius: 0 7px 7px 0; padding: 10px 14px; margin-bottom: 16px; font-size: 13px; color: #c02828; }
.vd-pw-success { background: #f0fdf4; border-left: 3px solid #059669; border-radius: 0 7px 7px 0; padding: 10px 14px; margin-bottom: 16px; font-size: 13px; font-weight: 600; color: #059669; }

/* ── States ── */
.vd-loading { padding: 48px; text-align: center; color: #6b7280; font-size: 15px; }
.vd-error   { padding: 48px; text-align: center; color: #6b7280; font-size: 15px; }
.vd-error button { color: #059669; background: none; border: none; cursor: pointer; font-weight: 700; text-decoration: underline; }

/* ── Nav count badge ── */
.vd-nav-count {
  margin-left: auto;
  background: rgba(255,255,255,.2);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  border-radius: 10px;
  padding: 1px 7px;
  min-width: 20px;
  text-align: center;
}

/* ── PO summary row ── */
.vd-po-summary-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}
.vd-po-stat {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 16px 18px;
  text-align: center;
}
.vd-po-stat--blue  { border-color: #bfdbfe; background: #eff6ff; }
.vd-po-stat--amber { border-color: #fcd34d; background: #fffbeb; }
.vd-po-stat--green { border-color: #bbf7d0; background: #f0fdf4; }
.vd-po-stat-val   { font-size: 26px; font-weight: 800; color: #1a2b45; line-height: 1; margin-bottom: 4px; }
.vd-po-stat--blue  .vd-po-stat-val  { color: #1e40af; }
.vd-po-stat--amber .vd-po-stat-val  { color: #92400e; }
.vd-po-stat--green .vd-po-stat-val  { color: #064e3b; }
.vd-po-stat-label { font-size: 11.5px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: .04em; }

/* ── PO filter tabs ── */
.vd-po-tabs {
  display: flex; gap: 4px; flex-wrap: wrap;
  background: #f0f4f8; border-radius: 8px; padding: 4px; margin-bottom: 16px;
}
.vd-po-tab {
  padding: 6px 14px; border: none; background: transparent;
  border-radius: 6px; font-size: 13px; font-weight: 500; color: #7a90b0;
  cursor: pointer; transition: background .12s, color .12s; white-space: nowrap;
  font-family: inherit;
}
.vd-po-tab:hover { background: #fff; color: #374151; }
.vd-po-tab--active { background: #fff; color: #064e3b; font-weight: 700; box-shadow: 0 1px 4px rgba(0,0,0,.08); }

/* ── Event / PO card list ── */
.vd-ev-list { display: flex; flex-direction: column; gap: 12px; }
.vd-po-list { display: flex; flex-direction: column; gap: 12px; }
.vd-po-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  transition: box-shadow .15s;
}
.vd-po-card:hover { box-shadow: 0 2px 12px rgba(0,0,0,.07); }

.vd-po-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  cursor: pointer;
  gap: 12px;
}
.vd-po-card-left  { display: flex; align-items: center; gap: 14px; flex: 1; min-width: 0; }
.vd-po-icon-wrap  { width: 40px; height: 40px; border-radius: 10px; background: #f0fdf4; border: 1px solid #bbf7d0; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.vd-po-icon       { font-size: 18px; }
.vd-po-card-title-row { display: flex; align-items: center; gap: 10px; margin-bottom: 4px; flex-wrap: wrap; }
.vd-po-number     { font-family: monospace; font-size: 14px; font-weight: 800; color: #064e3b; }
.vd-po-card-meta  { font-size: 12.5px; color: #6b7280; display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.vd-po-meta-sep   { color: #d1d5db; }
.vd-po-card-right { display: flex; align-items: center; gap: 12px; flex-shrink: 0; }
.vd-po-total      { font-size: 15px; font-weight: 800; color: #1a2b45; white-space: nowrap; font-variant-numeric: tabular-nums; }
.vd-po-chevron    { font-size: 18px; color: #9ca3af; transition: transform .2s; display: inline-block; }
.vd-po-chevron--open { transform: rotate(180deg); }

/* Badge statuses for PO */
.vd-badge--issued             { background: #dbeafe; color: #1e40af; }
.vd-badge--partially_received { background: #fef3c7; color: #92400e; }
.vd-badge--completed          { background: #d1fae5; color: #064e3b; }
.vd-badge--closed             { background: #e0e7ff; color: #3730a3; }

/* ── PO detail (expanded) ── */
.vd-po-detail {
  border-top: 1px solid #f3f4f6;
  padding: 16px 20px 20px;
  background: #fafafa;
}
.vd-po-detail-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 16px;
}
.vd-po-meta-chip {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 8px 12px;
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 160px;
}
.vd-po-meta-chip--full { flex: 1 1 100%; }
.vd-po-meta-chip-label { font-size: 10.5px; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; color: #9ca3af; }
.vd-po-meta-chip-val   { font-size: 13px; font-weight: 600; color: #1a2b45; }
.vd-po-lines-title {
  font-size: 13px; font-weight: 700; color: #374151;
  margin: 0 0 10px; display: flex; align-items: center; gap: 8px;
}
.vd-po-lines-count {
  background: #e5e7eb; color: #374151; border-radius: 10px;
  font-size: 11px; font-weight: 700; padding: 1px 7px;
}
.vd-po-lines-table { font-size: 13px; }
.vd-po-line-spec { display: block; font-size: 11.5px; color: #9ca3af; margin-top: 2px; }

/* ── Table helpers ── */
.vd-ta-r       { text-align: right; font-variant-numeric: tabular-nums; }
.vd-fw-bold    { font-weight: 700; }
.vd-text-muted { color: #9ca3af; }
.vd-tfoot-label { text-align: right; font-size: 11.5px; font-weight: 700; color: #6b7280; text-transform: uppercase; letter-spacing: .04em; }
.vd-tfoot-val   { text-align: right; font-size: 13.5px; font-weight: 700; color: #1a2b45; white-space: nowrap; font-variant-numeric: tabular-nums; }
.vd-tfoot-total { background: #f0fdf4; }
.vd-tfoot-total-val { font-size: 15px !important; color: #064e3b !important; font-weight: 800 !important; }

/* ── Quote section ── */
.vd-quote-section { margin-top: 20px; padding-top: 16px; border-top: 1.5px dashed #e5e7eb; }

.vd-quote-declined {
  background: #fff5f5; border-left: 3px solid #fca5a5; border-radius: 0 8px 8px 0;
  padding: 10px 14px; font-size: 13px; color: #991b1b; font-weight: 600;
}

.vd-quote-submitted-banner {
  background: #f0fdf4; border-left: 3px solid #059669; border-radius: 0 8px 8px 0;
  padding: 10px 14px; font-size: 13px; font-weight: 600; color: #065f46;
  margin-bottom: 14px;
}
.vd-quote-submitted-date { font-weight: 400; color: #6b7280; margin-left: 4px; }

.vd-btn-open-quote {
  display: inline-flex; align-items: center; gap: 6px;
  background: #004AAD; color: #fff; border: none; border-radius: 8px;
  padding: 10px 22px; font-size: 13px; font-weight: 700; cursor: pointer;
  font-family: inherit; transition: background .15s;
}
.vd-btn-open-quote:hover { background: #003b8e; }

.vd-quote-form-head {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 14px;
  font-size: 14px; font-weight: 700; color: #1a2b45;
}
.vd-quote-currency-wrap { display: flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 600; color: #374151; }
.vd-quote-currency-select {
  padding: 6px 10px; border: 1.5px solid #dde5f0; border-radius: 7px;
  font-size: 13px; font-family: inherit; outline: none; background: #fff;
}
.vd-quote-currency-select:focus { border-color: #004AAD; }

.vd-quote-table th, .vd-quote-table td { white-space: nowrap; }
.vd-quote-req { color: #dc2626; }

.vd-quote-input {
  padding: 7px 10px; border: 1.5px solid #dde5f0; border-radius: 7px;
  font-size: 13px; width: 100px; outline: none; font-family: inherit;
  box-sizing: border-box;
}
.vd-quote-input--sm { width: 70px; }
.vd-quote-input:focus { border-color: #004AAD; }

.vd-quote-notes-row { margin-bottom: 14px; }
.vd-quote-notes-label { display: block; font-size: 12.5px; font-weight: 600; color: #374151; margin-bottom: 5px; }
.vd-quote-notes-input {
  width: 100%; padding: 9px 12px; border: 1.5px solid #dde5f0; border-radius: 8px;
  font-size: 13px; font-family: inherit; outline: none; resize: vertical;
  box-sizing: border-box;
}
.vd-quote-notes-input:focus { border-color: #004AAD; }

.vd-quote-actions {
  display: flex; align-items: center; gap: 12px;
  padding-top: 14px; border-top: 1px solid #f0f4f8;
}
.vd-btn-submit-quote {
  padding: 10px 28px; background: #004AAD; color: #fff; border: none; border-radius: 8px;
  font-size: 14px; font-weight: 700; cursor: pointer; font-family: inherit; transition: background .15s;
}
.vd-btn-submit-quote:hover:not(:disabled) { background: #003b8e; }
.vd-btn-submit-quote:disabled { opacity: .5; cursor: not-allowed; }

/* View my bid button */
.vd-btn-view-bid {
  display: inline-flex; align-items: center; gap: 6px;
  background: #f0fdf4; color: #059669; border: 1.5px solid #bbf7d0;
  border-radius: 8px; padding: 8px 18px; font-size: 13px; font-weight: 600;
  cursor: pointer; font-family: inherit; transition: background .15s; margin-top: 10px;
}
.vd-btn-view-bid:hover { background: #dcfce7; }

.vd-bid-action-row {
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
}
.vd-btn-open-quote--sm {
  padding: 8px 18px; font-size: 13px; margin-top: 10px;
}

/* Read-only bid view */
.vd-bid-view { margin-top: 14px; }
.vd-bid-view-meta {
  display: flex; gap: 12px; flex-wrap: wrap; margin-bottom: 14px;
}
.vd-bid-meta-chip {
  background: #f8fafc; border: 1px solid #e5e7eb; border-radius: 8px;
  padding: 8px 14px; display: flex; flex-direction: column; gap: 2px;
}
.vd-bid-meta-label { font-size: 10.5px; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; color: #9ca3af; }
.vd-bid-meta-val   { font-size: 13px; font-weight: 600; color: #1a2b45; }
.vd-bid-total      { color: #059669; font-size: 15px; }

.vd-bid-cover-note {
  margin-top: 14px; background: #f8fafc; border: 1px solid #e5e7eb;
  border-radius: 8px; padding: 12px 14px;
}

.vd-btn-decline {
  padding: 9px 18px; background: #fff; color: #dc2626; border: 1.5px solid #fca5a5;
  border-radius: 8px; font-size: 13px; font-weight: 600; cursor: pointer;
  font-family: inherit; transition: background .15s;
}
.vd-btn-decline:hover:not(:disabled) { background: #fff5f5; }
.vd-btn-decline:disabled { opacity: .5; cursor: not-allowed; }

/* ── Bid locked note ── */
.vd-bid-locked-note {
  font-size: 12px; color: #9ca3af; font-style: italic; padding: 4px 0;
}

/* ── Bid confirm modal ── */
.vd-modal-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,.45); z-index: 1000;
  display: flex; align-items: center; justify-content: center; padding: 16px;
}
.vd-modal-box {
  background: #fff; border-radius: 14px; box-shadow: 0 20px 60px rgba(0,0,0,.18);
  width: 100%; animation: vd-modal-in .2s ease;
}
.vd-modal-box--sm { max-width: 460px; }
@keyframes vd-modal-in {
  from { opacity: 0; transform: scale(.95) translateY(8px); }
  to   { opacity: 1; transform: scale(1) translateY(0); }
}
.vd-modal-header--primary {
  background: linear-gradient(135deg, #004aad, #0066ff);
  border-radius: 14px 14px 0 0;
  padding: 22px;
  display: flex; justify-content: center; align-items: center;
}
.vd-modal-header--invoice {
  background: linear-gradient(135deg, #059669, #10b981);
  border-radius: 14px 14px 0 0;
  padding: 22px;
  display: flex; justify-content: center; align-items: center;
}
.vd-modal-header-icon {
  width: 52px; height: 52px; background: rgba(255,255,255,.2);
  border-radius: 50%; display: flex; align-items: center; justify-content: center; color: #fff;
}
.vd-modal-body--center { padding: 28px 32px 20px; text-align: center; }
.vd-modal-confirm-title {
  font-size: 18px; font-weight: 700; color: #1a2b45; margin-bottom: 12px;
}
.vd-modal-confirm-desc {
  font-size: 13px; color: #4b5563; line-height: 1.6; margin-bottom: 20px;
}
.vd-modal-confirm-meta {
  background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px;
  padding: 12px 16px; text-align: left;
}
.vd-modal-confirm-meta-row {
  display: flex; justify-content: space-between; align-items: center;
  padding: 5px 0; font-size: 13px; border-bottom: 1px solid #f1f5f9;
}
.vd-modal-confirm-meta-row:last-child { border-bottom: none; }
.vd-modal-confirm-meta-label { color: #6b7280; }
.vd-modal-confirm-meta-val   { font-weight: 600; color: #1a2b45; }
.vd-modal-footer {
  display: flex; gap: 10px; padding: 16px 32px 24px; justify-content: center;
}
.vd-modal-btn {
  padding: 10px 28px; border-radius: 8px; font-size: 14px; font-weight: 600;
  cursor: pointer; border: none; font-family: inherit; transition: background .15s;
}
.vd-modal-btn--cancel {
  background: #f1f5f9; color: #374151; border: 1.5px solid #e2e8f0;
}
.vd-modal-btn--cancel:hover { background: #e2e8f0; }
.vd-modal-btn--confirm {
  background: #004aad; color: #fff;
}
.vd-modal-btn--confirm:hover { background: #0057cc; }
.vd-modal-btn--invoice {
  background: #059669; color: #fff;
}
.vd-modal-btn--invoice:hover { background: #047857; }

/* ── Clarifications ── */
.vd-clarif-section {
  margin-top: 28px;
  border-top: 1px solid #e5e7eb;
  padding-top: 20px;
}
.vd-clarif-header {
  display: flex; align-items: center; gap: 8px; margin-bottom: 14px;
}
.vd-clarif-title {
  font-size: 14px; font-weight: 700; color: #1a2b45;
}
.vd-clarif-count {
  background: #004aad; color: #fff; border-radius: 12px;
  padding: 1px 8px; font-size: 11px; font-weight: 700;
}
.vd-clarif-list {
  display: flex; flex-direction: column; gap: 12px; margin-bottom: 16px;
}
.vd-clarif-item {
  border: 1px solid #e5e7eb; border-radius: 8px; overflow: hidden;
}
.vd-clarif-item--mine {
  border-color: #bfdbfe;
}
.vd-clarif-q {
  display: flex; gap: 12px; padding: 12px 14px; background: #f8fafc;
}
.vd-clarif-a {
  display: flex; gap: 12px; padding: 12px 14px; background: #ecfdf5;
  border-top: 1px solid #d1fae5;
}
.vd-clarif-icon {
  flex-shrink: 0; width: 24px; height: 24px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 11px; font-weight: 800;
}
.vd-clarif-icon--q { background: #dbeafe; color: #1d4ed8; }
.vd-clarif-icon--a { background: #a7f3d0; color: #065f46; }
.vd-clarif-body { flex: 1; min-width: 0; }
.vd-clarif-text { font-size: 13px; color: #1a2b45; line-height: 1.55; white-space: pre-wrap; }
.vd-clarif-meta {
  display: flex; align-items: center; flex-wrap: wrap; gap: 6px;
  font-size: 11px; color: #6b7280; margin-top: 5px;
}
.vd-clarif-badge {
  padding: 1px 7px; border-radius: 10px; font-size: 10px; font-weight: 700; letter-spacing: .03em;
}
.vd-clarif-badge--public { background: #dbeafe; color: #1d4ed8; }
.vd-clarif-badge--pending { background: #fef3c7; color: #92400e; }
.vd-clarif-form { margin-top: 14px; }
.vd-clarif-form-label {
  display: block; font-size: 13px; font-weight: 600; color: #374151; margin-bottom: 6px;
}
.vd-clarif-form-footer {
  display: flex; align-items: center; justify-content: space-between;
  gap: 10px; margin-top: 8px;
}
.vd-clarif-success {
  font-size: 12px; color: #065f46; font-weight: 500;
}

/* ── Invoice row ── */
.vd-invoice-row {
  display: flex; align-items: center; gap: 10px;
  padding: 14px 0 2px; border-top: 1px solid #e5e7eb; margin-top: 16px;
}
.vd-invoice-badge {
  font-size: 12px; font-weight: 600; color: #065f46;
  background: #d1fae5; border: 1px solid #6ee7b7;
  border-radius: 20px; padding: 3px 12px;
}
.vd-btn-invoice {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 18px; border-radius: 8px; font-size: 13px; font-weight: 600;
  cursor: pointer; border: 1.5px solid #6ee7b7;
  background: #ecfdf5; color: #065f46; transition: background .15s;
}
.vd-btn-invoice:hover:not(:disabled) { background: #d1fae5; }
.vd-btn-invoice:disabled { opacity: .5; cursor: not-allowed; }
.vd-btn-invoice--view { background: #eff6ff; border-color: #bfdbfe; color: #1d4ed8; }
.vd-btn-invoice--view:hover { background: #dbeafe; }

/* ── Responsive ── */
@media (max-width: 900px) { .vd-form-grid { grid-template-columns: 1fr; } }
@media (max-width: 640px) { .vd-po-summary-row { grid-template-columns: 1fr 1fr; } }
@media (max-width: 700px) { .vd-sidebar { display: none; } }
</style>
